import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { format } from "date-fns"
import { BellOff, Crosshair, ExternalLink, Eye } from "lucide-react"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { LicensePlate } from "@/components/ui/license-plate"
import LightMapView, {
  LightMapViewHandle,
} from "@/components/maps/LightMapView"
import Marker from "@/components/maps/mapLibre/Marker"
import { Popup } from "@/components/maps/mapLibre/Popup"
import DriverLocationMarker from "@/components/maps/mapLibre/DriverLocationMarker"
import ActionMarker, {
  ActionType,
} from "@/components/maps/mapLibre/ActionMarker"
import ChangeMapStyle from "@/components/maps/ChangeMapStyle"
import type { MapStyle } from "@/components/maps/MapManager"
import { useFuelAlertDetail } from "./useFuelAlertDetail"
import { FuelSparkline, type SparkMarker } from "./FuelSparkline"
import { FuelMapLegend } from "./FuelReportLegend"
import { useDriverRouteAtTime } from "./useDriverRouteAtTime"
import { FuelRouteLayer } from "./FuelRouteLayer"
import { PlaybackSlider, interpolateAtIndex } from "./PlaybackSlider"
import { verdictDisplay, formatFuelFraction } from "./verdictDisplay"
import { computeEventBounds } from "./fuelEventBounds"
import { parseUtcIso } from "./parseUtcIso"
import type { LatLng, TelemetryPoint } from "./fuelReportTypes"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { getDateFnsLocale } from "@/lib/i18n/format"

function dot(color: string) {
  return {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: color,
    border: "2px solid white",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.3)",
  } as const
}

function gmapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat},${lng}`
}

function ZoomButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      title={t("reports.fuelDetail.zoomHere")}
      className="text-muted-foreground hover:text-foreground"
    >
      <Crosshair className="h-4 w-4" />
    </button>
  )
}

function MapLink({
  lat,
  lng,
  href,
}: {
  lat: number
  lng: number
  href?: string
}) {
  const { t } = useTranslation()
  return (
    <a
      href={href || gmapsUrl(lat, lng)}
      target="_blank"
      rel="noopener noreferrer"
      title={t("reports.fuelDetail.openInGoogleMaps")}
      className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
    >
      <ExternalLink className="h-4 w-4" />
    </a>
  )
}

/** Label/value line for the panel's uppercase-label rows (SENT, TIME, ...). */
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{value}</span>
    </div>
  )
}

/** Rich station row: brand/category, address, detour — falls back to coords. */
/** Station headline: `name` first, because for some catalogs `brand` is a
 *  placeholder ("DIVERSE / OTHERS") while the real identity is the name. */
export function stationTitle(s: LatLng): string {
  return [s.name ?? s.brand, s.category].filter(Boolean).join(" · ")
}

/** Street and city as one line — each may be missing on its own. */
export function stationAddress(s: LatLng): string {
  return [s.address, s.city].filter(Boolean).join(", ")
}

const detourText = (t: TFunction, s: LatLng): string => {
  if (s.detourKm != null && s.detourMin != null)
    return t("reports.fuelDetail.detourKmMin", {
      km: s.detourKm,
      min: s.detourMin,
    })
  if (s.detourKm != null)
    return t("reports.fuelDetail.detourKm", { km: s.detourKm })
  return t("reports.fuelDetail.detourMin", { min: s.detourMin })
}

function StationInfo({ s }: { s: LatLng }) {
  const { t } = useTranslation()
  const title = stationTitle(s)
  const address = stationAddress(s)
  return (
    <div className="flex flex-col">
      <span className="text-foreground">
        {title || `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`}
      </span>
      {address && (
        <span className="text-xs text-muted-foreground">{address}</span>
      )}
      {s.pricePerLitre != null && (
        <span className="text-xs text-muted-foreground">
          {s.pricePerLitre.toFixed(3)}
          {s.currency ? ` ${s.currency}` : ""}/l
          {s.fuelPartner ? ` · ${s.fuelPartner}` : ""}
        </span>
      )}
      {(s.detourKm != null || s.detourMin != null) && (
        <span className="text-xs text-muted-foreground">
          {detourText(t, s)}
        </span>
      )}
    </div>
  )
}

/** What the map hover popup shows for a marker. */
type HoverInfo = {
  lng: number
  lat: number
  title: string
  subtitle?: string
  /** e.g. "Fuel 48%" / "Fuel 46% → 98%" — omitted when unknown. */
  fuel?: string
}

// A telemetry sample this far from the marker's timestamp no longer says
// anything about the fuel level at that moment.
const FUEL_SAMPLE_MAX_GAP_MS = 30 * 60 * 1000

/** Fuel level from the telemetry sample nearest to `iso`, or null. */
export function fuelLevelAt(
  telemetry: TelemetryPoint[],
  iso: string,
): number | null {
  const t = parseUtcIso(iso).getTime()
  if (Number.isNaN(t)) return null
  let best: number | null = null
  let bestGap = FUEL_SAMPLE_MAX_GAP_MS
  for (const p of telemetry) {
    if (p.fuelLevel == null) continue
    const gap = Math.abs(parseUtcIso(p.timestamp).getTime() - t)
    if (gap <= bestGap) {
      bestGap = gap
      best = p.fuelLevel
    }
  }
  return best
}

function fmtTime(iso: string): string {
  const d = parseUtcIso(iso)
  return Number.isNaN(d.getTime())
    ? "—"
    : format(d, "dd MMM yyyy HH:mm", { locale: getDateFnsLocale() })
}

/** "12 min" / "2h 5m" between two timestamps, or null when either is
 * unparseable or `toIso` precedes `fromIso`. */
function fmtDuration(
  t: TFunction,
  fromIso: string,
  toIso: string,
): string | null {
  const from = parseUtcIso(fromIso).getTime()
  const to = parseUtcIso(toIso).getTime()
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return null
  const mins = Math.round((to - from) / 60000)
  if (mins < 60) return t("reports.fuelDetail.minutes", { minutes: mins })
  return t("reports.fuelDetail.hoursMinutes", {
    hours: Math.floor(mins / 60),
    minutes: mins % 60,
  })
}

/** "+12 min" / "+2h 5m" between the alert send time and when it was seen. */
function fmtDelay(
  t: TFunction,
  sentIso: string,
  seenIso: string,
): string | null {
  const d = fmtDuration(t, sentIso, seenIso)
  return d ? `+${d}` : null
}

function fmtGap(seconds: number): string {
  return seconds < 90 ? `${seconds}s` : `${Math.round(seconds / 60)} min`
}

/** "512 m" / "83.4 km" — raw meter counts get unreadable past a kilometer. */
function fmtDistance(meters: number): string {
  return meters < 1000 ? `${meters} m` : `${(meters / 1000).toFixed(1)} km`
}

/**
 * The earlier of the event and its correlated partner (or just the event when
 * there's no valid partner). The route is loaded as of this moment so a
 * correlated alert↔refuel shows the same route — the one active when the alert
 * was sent — whichever side you open, rather than the (later, possibly shorter)
 * route at refuel time.
 */
export function earlierTimestamp(
  eventAt: string,
  partnerAt: string | undefined,
): string {
  const ta = parseUtcIso(eventAt).getTime()
  const tb = partnerAt ? parseUtcIso(partnerAt).getTime() : NaN
  if (Number.isNaN(tb)) return eventAt
  if (Number.isNaN(ta)) return partnerAt as string
  return ta <= tb ? eventAt : (partnerAt as string)
}

// gps get_fuel_telemetry rejects ranges longer than 1 day.
const MAX_TELEMETRY_SPAN_MS = 24 * 60 * 60 * 1000

/**
 * Telemetry window for the detail playback. With a correlated partner
 * (alert↔refuel) it spans BOTH timestamps ±30 min, so the same range is shown
 * whichever side is opened; with just the event it's ±30 min around it. A
 * weekend-spanning pair (72h correlation) would exceed the backend's 1-day
 * telemetry cap — then the window collapses to ±30 min around the opened
 * event only. Falls back to the given window when no timestamp parses.
 */
export function computeTelemetryWindow(
  eventAt: string,
  partnerAt: string | undefined,
  fallback: { from: string; to: string },
): { from: string; to: string } {
  const pad = 30 * 60 * 1000
  const times = [eventAt, partnerAt]
    .map((t) => (t ? parseUtcIso(t).getTime() : NaN))
    .filter((t) => !Number.isNaN(t))
  if (times.length === 0) return fallback
  let from = Math.min(...times) - pad
  let to = Math.max(...times) + pad
  if (to - from > MAX_TELEMETRY_SPAN_MS) {
    const event = parseUtcIso(eventAt).getTime()
    const center = Number.isNaN(event) ? times[0] : event
    from = center - pad
    to = center + pad
  }
  return {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
  }
}

export function FuelEventDetailDialog({
  open,
  driverJid,
  driverName,
  driverPlate,
  eventKind,
  eventId,
  eventAt,
  window,
  partnerAt,
  suppressed,
  reason,
  onOpenChange,
}: {
  open: boolean
  driverJid: string
  /** Resolved display name for the header; falls back to a generic title. */
  driverName?: string
  /** Vehicle plate for the header line; omitted when no plate is trustworthy. */
  driverPlate?: string
  /** Whether the opened event is the alert or the refuel side of the pair. */
  eventKind?: "alert" | "refuel"
  eventId: string
  /** Timestamp of the event (alert sent / refuel time) — used to load the
   * route as it looked at that moment. */
  eventAt: string
  window: { from: string; to: string }
  /** Timestamp of the correlated event (the linked refuel for an alert, or the
   * linked alert for a refuel), when there is one. The telemetry window spans
   * both so the same range is shown whichever side you open. */
  partnerAt?: string
  /** This alert was never sent (e.g. no usable route) — see reason. */
  suppressed?: boolean
  reason?: string | null
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  // Telemetry playback is scoped to the event(s), not the whole day — otherwise
  // the driver dot starts far away at the day's first point. When the event has
  // a correlated partner (alert↔refuel), span BOTH timestamps so the same range
  // shows whichever side you open; otherwise ±30 min around the single event.
  const telemetryWindow = useMemo(
    () => computeTelemetryWindow(eventAt, partnerAt, window),
    [eventAt, partnerAt, window],
  )
  const { detail, telemetry, isLoading, error, telemetryError } =
    useFuelAlertDetail(driverJid, eventId, telemetryWindow)
  // Load the route as of the earlier of the pair (the alert time for a
  // correlated alert↔refuel) so the same route shows whichever side you open.
  const routeAt = useMemo(
    () => earlierTimestamp(eventAt, partnerAt),
    [eventAt, partnerAt],
  )
  const { coordinates: routeCoords, stops: routeStops } = useDriverRouteAtTime(
    driverJid,
    routeAt,
    open,
  )
  const mapRef = useRef<LightMapViewHandle>(null)
  const [playIndex, setPlayIndex] = useState(0)
  const [mapStyle, setMapStyle] = useState<MapStyle>("default")
  // Marker under the cursor — shown in a map popup instead of the browser's
  // native title tooltip.
  const [hover, setHover] = useState<HoverInfo | null>(null)
  const playPos = interpolateAtIndex(telemetry, playIndex)
  const v = detail ? verdictDisplay(detail.verdict) : null
  const refuelColor = detail?.verdict === "MATCH" ? "#14ae5c" : "#f59e0b"
  const bounds = useMemo(() => {
    const eventBounds = computeEventBounds(detail, telemetry)
    if (routeCoords.length === 0) return eventBounds
    let minLng = Infinity
    let minLat = Infinity
    let maxLng = -Infinity
    let maxLat = -Infinity
    for (const [lng, lat] of routeCoords) {
      minLng = Math.min(minLng, lng)
      minLat = Math.min(minLat, lat)
      maxLng = Math.max(maxLng, lng)
      maxLat = Math.max(maxLat, lat)
    }
    const routeBounds: [[number, number], [number, number]] = [
      [minLng, minLat],
      [maxLng, maxLat],
    ]
    if (!eventBounds) return routeBounds
    return [
      [
        Math.min(eventBounds[0][0], routeBounds[0][0]),
        Math.min(eventBounds[0][1], routeBounds[0][1]),
      ],
      [
        Math.max(eventBounds[1][0], routeBounds[1][0]),
        Math.max(eventBounds[1][1], routeBounds[1][1]),
      ],
    ] as [[number, number], [number, number]]
  }, [detail, telemetry, routeCoords])
  const refuel = detail?.refuel
  const hasRefuelPoint =
    refuel != null && refuel.lat != null && refuel.lng != null
  const seen = detail?.seen ?? null
  // The seen delay is always measured from the ALERT send time — when this
  // dialog was opened from the refuel side, that's the partner timestamp.
  const alertAt = eventKind === "refuel" ? partnerAt : eventAt
  const seenDelay =
    seen?.seen && seen.seenAt && alertAt
      ? fmtDelay(t, alertAt, seen.seenAt)
      : null

  // Info-panel section visibility: the Alert section only makes sense once
  // there's an alert timestamp; Refueling shows for an actual refuel, or for
  // an alert-only event whose verdict says none happened.
  const showAlert = !!alertAt
  const showRefueling = suppressed
    ? refuel != null
    : refuel != null || detail?.verdict === "NO_REFUEL"
  // REACTION row text under the Alert section.
  const refuelReaction = refuel
    ? (() => {
        const d = alertAt ? fmtDuration(t, alertAt, refuel.ts) : null
        return d ? t("reports.fuelDetail.refueledLater", { duration: d }) : "—"
      })()
    : detail?.verdict === "NO_REFUEL"
      ? t("reports.fuelDetail.noRefuel")
      : "—"

  // Fuel levels for the hover popups: the refuel carries its own before/after;
  // alert and seen levels come from the nearest telemetry sample.
  const alertLevel = alertAt ? fuelLevelAt(telemetry, alertAt) : null
  const alertFuel =
    alertLevel != null
      ? t("reports.fuelDetail.fuelLevel", {
          level: formatFuelFraction(alertLevel),
        })
      : undefined
  const seenLevel =
    seen?.seen && seen.seenAt ? fuelLevelAt(telemetry, seen.seenAt) : null
  const seenFuel =
    seenLevel != null
      ? t("reports.fuelDetail.fuelLevel", {
          level: formatFuelFraction(seenLevel),
        })
      : undefined
  const refuelFuel = refuel
    ? t("reports.fuelDetail.fuelChange", {
        before: formatFuelFraction(refuel.fuelBefore),
        after: formatFuelFraction(refuel.fuelAfter),
      })
    : undefined

  // Vertical event markers on the fuel sparkline.
  const sparkMarkers = useMemo(() => {
    const out: SparkMarker[] = []
    if (alertAt) out.push({ at: alertAt, kind: "alert" })
    const refuelTs =
      refuel?.ts ?? (eventKind === "refuel" ? eventAt : partnerAt)
    if (refuelTs) out.push({ at: refuelTs, kind: "refuel" })
    if (seen?.seen && seen.seenAt) out.push({ at: seen.seenAt, kind: "seen" })
    return out
  }, [alertAt, refuel?.ts, eventKind, eventAt, partnerAt, seen])

  const zoomTo = useCallback((lat: number, lng: number) => {
    mapRef.current?.getMap()?.flyTo({ center: [lng, lat], zoom: 15 })
  }, [])

  const hoverProps = useCallback(
    (info: HoverInfo) => ({
      onMouseEnter: () => setHover(info),
      onMouseLeave: () => setHover(null),
    }),
    [],
  )

  useEffect(() => {
    setPlayIndex(0)
  }, [telemetry])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] w-[95vw] max-w-[95vw] flex-row gap-0 p-0">
        <DialogTitle className="sr-only">
          {t("reports.fuelDetail.dialogTitle")}
        </DialogTitle>

        {/* Map (left) */}
        <div className="relative flex-1 overflow-hidden">
          <LightMapView
            ref={mapRef}
            fitBoundsTo={bounds}
            mapStyle={mapStyle}
            className="absolute inset-0"
          >
            <FuelRouteLayer coordinates={routeCoords} />
            {routeStops.map((stop, i) => (
              <ActionMarker
                key={`stop-${i}`}
                longitude={stop.lng}
                latitude={stop.lat}
                action={(stop.action ?? "other") as ActionType}
              />
            ))}
            {detail?.suggested.map((s, i) => (
              <Marker key={`sug-${i}`} longitude={s.lng} latitude={s.lat}>
                <div
                  style={dot("#14ae5c")}
                  {...hoverProps({
                    lng: s.lng,
                    lat: s.lat,
                    title:
                      stationTitle(s) ||
                      t("reports.fuelDetail.suggestedStation"),
                    subtitle: stationAddress(s) || undefined,
                  })}
                />
              </Marker>
            ))}
            {detail?.driverPos && (
              <Marker
                longitude={detail.driverPos.lng}
                latitude={detail.driverPos.lat}
              >
                <div
                  style={dot("#2563eb")}
                  {...hoverProps({
                    lng: detail.driverPos.lng,
                    lat: detail.driverPos.lat,
                    title: t("reports.fuelDetail.driverWhenAlerted"),
                    subtitle: alertAt ? fmtTime(alertAt) : undefined,
                    fuel: alertFuel,
                  })}
                />
              </Marker>
            )}
            {!suppressed &&
              detail?.seen?.seen &&
              detail.seen.seenLat != null &&
              detail.seen.seenLng != null && (
                <Marker
                  longitude={detail.seen.seenLng}
                  latitude={detail.seen.seenLat}
                  // Nudged up so it stays readable when the seen position
                  // coincides with the driver/refuel dot underneath.
                  offset={[0, -14]}
                >
                  <div
                    {...hoverProps({
                      lng: detail.seen.seenLng,
                      lat: detail.seen.seenLat,
                      title: t("reports.fuelDetail.driverSawHere"),
                      subtitle: t("reports.fuelDetail.nearestGpsFix", {
                        time: detail.seen.seenAt
                          ? fmtTime(detail.seen.seenAt)
                          : "",
                      }),
                      fuel: seenFuel,
                    })}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "white",
                      border: "2px solid #7c3aed",
                      boxShadow: "0 0 0 1px rgba(0,0,0,0.3)",
                    }}
                  >
                    <Eye size={12} color="#7c3aed" />
                  </div>
                </Marker>
              )}
            {hasRefuelPoint && (
              <Marker longitude={refuel!.lng!} latitude={refuel!.lat!}>
                <div
                  style={dot(refuelColor)}
                  {...hoverProps({
                    lng: refuel!.lng!,
                    lat: refuel!.lat!,
                    title: t("reports.fuelDetail.refuelPoint"),
                    subtitle:
                      refuel!.distanceM != null
                        ? t("reports.fuelDetail.timeDistanceFromStation", {
                            time: fmtTime(refuel!.ts),
                            distance: refuel!.distanceM,
                          })
                        : fmtTime(refuel!.ts),
                    fuel: refuelFuel,
                  })}
                />
              </Marker>
            )}
            {playPos && (
              <DriverLocationMarker
                longitude={playPos.lng}
                latitude={playPos.lat}
              />
            )}
            {hover && (
              <Popup
                longitude={hover.lng}
                latitude={hover.lat}
                anchor="bottom"
                offset={14}
                closeButton={false}
                closeOnClick={false}
                className="pointer-events-none"
              >
                <div className="flex flex-col rounded-md bg-white px-2.5 py-1.5 text-xs shadow-lg dark:bg-gray-800">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {hover.title}
                  </span>
                  {hover.subtitle && (
                    <span className="text-gray-500 dark:text-gray-400">
                      {hover.subtitle}
                    </span>
                  )}
                  {hover.fuel && (
                    <span className="text-gray-500 dark:text-gray-400">
                      {hover.fuel}
                    </span>
                  )}
                </div>
              </Popup>
            )}
          </LightMapView>
          <ChangeMapStyle
            currentStyle={mapStyle}
            onChangeMapStyle={setMapStyle}
          />
          <FuelMapLegend showSeen={!!seen?.seen} />
        </div>

        {/* Info panel (right) */}
        <div className="flex w-[360px] shrink-0 flex-col overflow-y-auto border-l">
          <div className="border-b p-4">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold">
                {driverName || t("reports.fuelDetail.fuelEvent")}
              </h2>
              {driverPlate && <LicensePlate plateNumber={driverPlate} />}
            </div>
            <div className="text-xs text-muted-foreground">
              {eventKind === "refuel"
                ? t("reports.fuelDetail.refuelAt", { time: fmtTime(eventAt) })
                : t("reports.fuelDetail.alertAt", { time: fmtTime(eventAt) })}
            </div>
          </div>
          <div className="flex flex-col gap-5 p-4 text-sm">
            {isLoading && (
              <div className="text-muted-foreground">
                {t("reports.shared.loading")}
              </div>
            )}
            {error && (
              <div className="text-red-500">
                {t("reports.shared.error", { error })}
              </div>
            )}

            {detail && v && (
              <>
                {showAlert && (
                  <div className="flex flex-col gap-3">
                    <h2 className="text-xl font-bold">
                      {t("reports.fuelDetail.alert")}
                    </h2>
                    {suppressed && (
                      <div className="flex items-center gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 p-2 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                        <BellOff className="h-4 w-4 shrink-0" />
                        <span className="font-medium">
                          {reason === "no_route"
                            ? t("reports.fuelDetail.notSentNoRoute")
                            : reason
                              ? t("reports.fuelDetail.notSentReason", {
                                  reason,
                                })
                              : t("reports.fuelDetail.notSent")}
                        </span>
                      </div>
                    )}
                    {/* A suppressed breach was detected, never sent — "SENT"
                        would misstate what happened. */}
                    <Row
                      label={
                        suppressed
                          ? t("reports.fuelDetail.rows.detected")
                          : t("reports.fuelDetail.rows.sent")
                      }
                      value={fmtTime(alertAt!)}
                    />
                    {!suppressed && (
                      <Row
                        label={t("reports.fuelDetail.rows.reaction")}
                        value={refuelReaction}
                      />
                    )}
                    <div className="flex flex-col gap-2">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t("reports.fuelDetail.rows.suggestedStations", {
                          count: detail.suggested.length,
                        })}
                      </div>
                      {detail.suggested.length === 0 && (
                        <div className="text-muted-foreground">
                          {t("reports.fuelDetail.noSuggestedStation")}
                        </div>
                      )}
                      {detail.suggested.map((s, i) => (
                        <div
                          key={`s-${i}`}
                          className="flex items-start justify-between gap-2"
                        >
                          <StationInfo s={s} />
                          <span className="flex shrink-0 items-center gap-2 pt-0.5">
                            <ZoomButton onClick={() => zoomTo(s.lat, s.lng)} />
                            <MapLink
                              lat={s.lat}
                              lng={s.lng}
                              href={s.googleMapsUrl}
                            />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {showAlert && showRefueling && <div className="border-t" />}

                {showRefueling && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold">
                        {t("reports.fuelDetail.refueling")}
                      </h2>
                      {hasRefuelPoint && (
                        <span className="flex items-center gap-2">
                          <ZoomButton
                            onClick={() => zoomTo(refuel!.lat!, refuel!.lng!)}
                          />
                          <MapLink lat={refuel!.lat!} lng={refuel!.lng!} />
                        </span>
                      )}
                    </div>
                    {!suppressed && (
                      <div
                        className="flex items-center gap-2 font-medium"
                        style={{ color: v.color }}
                      >
                        <span>{v.emoji}</span>
                        <span>{v.label}</span>
                      </div>
                    )}
                    {/* `closest` is the nearest SUGGESTED station (the one the
                        classification matched against) — it is only the
                        station actually used on a MATCH. Elsewhere we don't
                        know the real station; the heading's map links point at
                        the refuel coordinates. */}
                    {detail.verdict === "MATCH" && refuel?.closest && (
                      <div className="flex flex-col gap-2">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t("reports.fuelDetail.rows.stationUsed")}
                        </div>
                        <StationInfo s={refuel.closest} />
                      </div>
                    )}
                    {refuel != null && refuel.distanceM != null && (
                      <div className="text-muted-foreground">
                        {t("reports.fuelDetail.fromSuggestedStation", {
                          distance: fmtDistance(refuel.distanceM),
                        })}
                      </div>
                    )}
                    {refuel && (
                      <>
                        <Row
                          label={t("reports.fuelDetail.rows.time")}
                          value={fmtTime(refuel.ts)}
                        />
                        <Row
                          label={t("reports.fuelDetail.rows.fuel")}
                          value={`${formatFuelFraction(
                            refuel.fuelBefore,
                          )} → ${formatFuelFraction(refuel.fuelAfter)}`}
                        />
                      </>
                    )}
                  </div>
                )}

                {detail.driverPos && (
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold">
                      {t("reports.fuelDetail.driverWhenAlerted")}
                    </span>
                    <span className="flex items-center gap-2">
                      <ZoomButton
                        onClick={() =>
                          zoomTo(detail.driverPos!.lat, detail.driverPos!.lng)
                        }
                      />
                      <MapLink
                        lat={detail.driverPos.lat}
                        lng={detail.driverPos.lng}
                      />
                    </span>
                  </div>
                )}

                {!suppressed && seen && (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-base font-bold">
                        {t("reports.fuelDetail.seenByDriver")}
                      </span>
                      {seen.seen &&
                        seen.seenLat != null &&
                        seen.seenLng != null && (
                          <span className="flex items-center gap-2">
                            <ZoomButton
                              onClick={() =>
                                zoomTo(seen.seenLat!, seen.seenLng!)
                              }
                            />
                            <MapLink lat={seen.seenLat} lng={seen.seenLng} />
                          </span>
                        )}
                    </div>
                    {seen.seen && seen.seenAt ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <Eye className="h-3.5 w-3.5 text-purple-600" />
                          <span>{fmtTime(seen.seenAt)}</span>
                          {seenDelay && (
                            <span className="text-muted-foreground">
                              {t("reports.fuelDetail.afterSending", {
                                delay: seenDelay,
                              })}
                            </span>
                          )}
                        </div>
                        {seen.seenLat != null && seen.gpsSampleGapS != null && (
                          <div className="text-xs text-muted-foreground">
                            {t("reports.fuelDetail.locationGap", {
                              gap: fmtGap(seen.gpsSampleGapS),
                            })}
                          </div>
                        )}
                      </>
                    ) : seen.found ? (
                      <div className="text-muted-foreground">
                        {t("reports.fuelDetail.notSeenYet")}
                      </div>
                    ) : (
                      <div className="text-muted-foreground">
                        {t("reports.fuelDetail.alertNotFound")}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  <div className="text-base font-bold">
                    {t("reports.fuelDetail.telemetry")}
                  </div>
                  {telemetryError ? (
                    <div className="text-xs text-muted-foreground">
                      {t("reports.fuelDetail.telemetryError", {
                        error: telemetryError,
                      })}
                    </div>
                  ) : (
                    <>
                      <FuelSparkline
                        points={telemetry}
                        markers={sparkMarkers}
                        index={playIndex}
                        onIndexChange={setPlayIndex}
                      />
                      <PlaybackSlider
                        points={telemetry}
                        index={playIndex}
                        onIndexChange={setPlayIndex}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
